import { type LocalRestrictions } from "@interfaces/LandingPricing";
import type { CountryEntry } from "@interfaces/PricingData";
import { getCountryCodes, getCountryEntry } from "@utilities/pricing-data";

/**
 * Get country content from the generated pricing data by country code
 */
const getCountryContent = (countryCode: string): CountryEntry | undefined => {
  return getCountryEntry(countryCode);
};

/**
 * Extract local restrictions for a given country code
 */
export const getLocalRestrictions = (
  countryCode: string,
): LocalRestrictions | null => {
  return getCountryContent(countryCode)?.local_restrictions ?? null;
};

/**
 * Check if country should be hidden from purchase flow
 * Uses show_country from local_restrictions (false = hidden)
 * Falls back to locally_restricted for backward compatibility
 */
export const isCountryHidden = (countryCode: string): boolean => {
  const content = getCountryContent(countryCode);
  if (content === undefined) return false;
  const restrictions = content.local_restrictions;
  if (restrictions?.show_country !== undefined) {
    return !restrictions.show_country;
  }
  // Fallback to old behavior
  return !!content.locally_restricted;
};

/**
 * Check if popup should be shown for a country
 */
export const shouldShowPopup = (countryCode: string): boolean => {
  return getLocalRestrictions(countryCode)?.show_pop_up ?? false;
};

/**
 * Get popup title and text for a language
 */
export const getPopupContent = (
  countryCode: string,
  lang: string = "en",
): { title: string; text: string } | null =>
  popupContentOf(getLocalRestrictions(countryCode), lang);

/** Which product a page sells, for the restriction popup's wording. */
export type RestrictionProduct = "simcard" | "bne_go" | "vpn";

/** Popup text field for a product and language ("pop_up_text_en" for eSIM). */
const popupTextKey = (
  product: RestrictionProduct | undefined,
  lang: string,
): keyof LocalRestrictions =>
  `pop_up_${product === undefined ? "" : `${product}_`}text_${lang}` as keyof LocalRestrictions;

/**
 * Popup title and text of a restriction record for a language (English when
 * the language has none), or null when the record does not ask for the popup.
 * pts keeps one record per country with the eSIM text (`pop_up_text_<lang>`)
 * and, for the other products, `pop_up_simcard_text_<lang>`,
 * `pop_up_bne_go_text_<lang>` and `pop_up_vpn_text_<lang>` (the UAE one says
 * "SIMs are available for purchase only outside the UAE…"); the title is
 * shared. A product text falls back to its English, then to the eSIM text, so
 * a country without product wording reads as before.
 */
export const popupContentOf = (
  restrictions: LocalRestrictions | null,
  lang: string = "en",
  product?: RestrictionProduct,
): { title: string; text: string } | null => {
  if (restrictions === null || restrictions.show_pop_up !== true) return null;

  const read = (key: keyof LocalRestrictions): string => {
    const value = restrictions[key];
    return typeof value === "string" ? value : "";
  };
  const first = (...keys: Array<keyof LocalRestrictions>): string =>
    keys.map(read).find(value => value !== "") ?? "";

  const title = first(
    `pop_up_title_${lang}` as keyof LocalRestrictions,
    "pop_up_title_en" as keyof LocalRestrictions,
  );
  const text =
    product === undefined
      ? first(popupTextKey(undefined, lang), popupTextKey(undefined, "en"))
      : first(
          popupTextKey(product, lang),
          popupTextKey(product, "en"),
          popupTextKey(undefined, lang),
          popupTextKey(undefined, "en"),
        );
  return { title, text };
};

/**
 * Check if disclaimer should be shown
 */
export const shouldShowDisclaimer = (countryCode: string): boolean => {
  return getLocalRestrictions(countryCode)?.show_disclaimer ?? false;
};

/**
 * Get disclaimer text for a language
 */
export const getDisclaimerContent = (
  countryCode: string,
  lang: string = "en",
): string => {
  const restrictions = getLocalRestrictions(countryCode);
  if (restrictions === null || restrictions.show_disclaimer !== true) return "";

  const key = `disclaimer_text_${lang}` as keyof LocalRestrictions;
  const fallbackKey = "disclaimer_text_en" as keyof LocalRestrictions;
  const text = (restrictions[key] as string) ?? "";
  const fallbackText = (restrictions[fallbackKey] as string) ?? "";
  return text !== "" ? text : fallbackText;
};

export const checkNotResidentCountryList: Record<string, string> = {
  AE: "not_uae_resident", // United Arab Emirates
};

export const checkNeedToCheckNotResident = (coverages: string[]): string[] => {
  const restrictedCountryList = Object.keys(checkNotResidentCountryList);

  const needToCheckNotResident: string[] = [];
  if (coverages) {
    restrictedCountryList.forEach(countryCode => {
      const restrictedCountryFromCoverages = coverages.find(
        coverage => coverage.toUpperCase() === countryCode.toUpperCase(),
      );
      if (restrictedCountryFromCoverages) {
        needToCheckNotResident.push(
          restrictedCountryFromCoverages.toUpperCase(),
        );
      }
    });
  }
  return needToCheckNotResident;
};

/**
 * Get list of countries hidden from purchase flow
 * Uses show_country from local_restrictions
 */
export const hiddenCountriesToPurchaseOwnPlan = (): string[] => {
  return getCountryCodes().filter(id => isCountryHidden(id));
};

export const affiliateRestrictedCountries: Record<string, string[]> = {
  TISC: ["IT"],
};
